import { useState, useRef, useEffect } from 'react';
import { useStudioStore } from '../../store/useStudioStore';

export const AudioSlicer = () => {
  const {
    sliceMarkers,
    audioSlices,
    slicerAudioUrl,
    slicerAudioDuration,
    slicerSensitivity,
    setSlicerAudioUrl,
    setSlicerAudioDuration,
    setSlicerSensitivity,
    setSliceMarkers,
    addSliceMarker,
    removeSliceMarker,
    updateSliceMarker,
    setAudioSlices,
    clearSlicerState,
  } = useStudioStore();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCreatingSlices, setIsCreatingSlices] = useState(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [draggedMarkerId, setDraggedMarkerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);

  // Subtask 6.1: Analyze audio for transients
  const detectTransients = async (audioBuffer: AudioBuffer) => {
    setIsAnalyzing(true);
    setError(null);
    
    try {
      const channelData = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;
      const transients: number[] = [];
      
      // Calculate threshold based on sensitivity
      // Higher sensitivity = lower threshold = more slices
      const threshold = (1 - slicerSensitivity) * 0.5;
      const minSliceLength = 0.1; // 100ms minimum between slices
      
      // Detect transients by analyzing amplitude changes
      for (let i = 1; i < channelData.length; i++) {
        const diff = Math.abs(channelData[i] - channelData[i - 1]);
        
        if (diff > threshold) {
          const time = i / sampleRate;
          
          // Ensure minimum slice length
          if (transients.length === 0 || time - transients[transients.length - 1] > minSliceLength) {
            transients.push(time);
          }
        }
      }
      
      // Limit to 64 slices maximum
      const limitedTransients = transients.slice(0, 64);
      
      // Convert to slice markers
      const markers = limitedTransients.map((time) => ({
        id: crypto.randomUUID(),
        time,
      }));
      
      setSliceMarkers(markers);
      
      if (limitedTransients.length === 0) {
        setError('No transients detected. Try adjusting sensitivity.');
      } else if (transients.length > 64) {
        setError(`Detected ${transients.length} transients, limited to 64 slices.`);
      }
    } catch (err) {
      setError('Failed to analyze audio: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Load audio file
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setError(null);
    clearSlicerState();
    
    try {
      // Create audio context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      
      // Read file as array buffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Decode audio data
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      audioBufferRef.current = audioBuffer;
      
      // Create object URL for playback
      const url = URL.createObjectURL(file);
      setSlicerAudioUrl(url);
      setSlicerAudioDuration(audioBuffer.duration);
      
      // Generate waveform data for visualization
      const waveform = generateWaveform(audioBuffer);
      setWaveformData(waveform);
      
      // Auto-detect transients
      await detectTransients(audioBuffer);
    } catch (err) {
      setError('Failed to load audio: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Generate waveform data for visualization
  const generateWaveform = (audioBuffer: AudioBuffer): number[] => {
    const channelData = audioBuffer.getChannelData(0);
    const samples = 500; // Number of waveform points
    const blockSize = Math.floor(channelData.length / samples);
    const waveform: number[] = [];
    
    for (let i = 0; i < samples; i++) {
      const start = i * blockSize;
      const end = start + blockSize;
      let sum = 0;
      
      for (let j = start; j < end && j < channelData.length; j++) {
        sum += Math.abs(channelData[j]);
      }
      
      waveform.push(sum / blockSize);
    }
    
    return waveform;
  };

  // Subtask 6.2: Display slice markers on waveform
  useEffect(() => {
    if (!canvasRef.current || waveformData.length === 0) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    const width = rect.width;
    const height = rect.height;
    
    // Clear canvas
    ctx.fillStyle = '#18181b';
    ctx.fillRect(0, 0, width, height);
    
    // Draw waveform
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 1;
    ctx.beginPath();
    
    const maxAmplitude = Math.max(...waveformData);
    waveformData.forEach((amplitude, i) => {
      const x = (i / waveformData.length) * width;
      const y = height / 2 - (amplitude / maxAmplitude) * (height / 2 - 10);
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    
    ctx.stroke();
    
    // Draw slice markers
    sliceMarkers.forEach((marker) => {
      const x = (marker.time / slicerAudioDuration) * width;
      
      // Draw marker line
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      
      // Draw marker handle
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(x - 4, 0, 8, 12);
    });
  }, [waveformData, sliceMarkers, slicerAudioDuration]);

  // Handle marker dragging
  const handleCanvasMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const clickTime = (x / rect.width) * slicerAudioDuration;
    
    // Check if clicking near a marker (within 10px)
    const clickedMarker = sliceMarkers.find((marker) => {
      const markerX = (marker.time / slicerAudioDuration) * rect.width;
      return Math.abs(x - markerX) < 10;
    });
    
    if (clickedMarker) {
      setDraggedMarkerId(clickedMarker.id);
    } else {
      // Add new marker
      addSliceMarker(clickTime);
    }
  };

  const handleCanvasMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!draggedMarkerId || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const newTime = Math.max(0, Math.min((x / rect.width) * slicerAudioDuration, slicerAudioDuration));
    
    updateSliceMarker(draggedMarkerId, newTime);
  };

  const handleCanvasMouseUp = () => {
    setDraggedMarkerId(null);
  };

  // Subtask 6.3: Create slices
  const createSlices = async () => {
    if (!audioBufferRef.current || sliceMarkers.length === 0) {
      setError('No audio loaded or no slice markers');
      return;
    }
    
    setIsCreatingSlices(true);
    setError(null);
    
    try {
      const audioBuffer = audioBufferRef.current;
      const slices: typeof audioSlices = [];
      
      // Sort markers by time
      const sortedMarkers = [...sliceMarkers].sort((a, b) => a.time - b.time);
      
      for (let i = 0; i < sortedMarkers.length; i++) {
        const startTime = sortedMarkers[i].time;
        const endTime = i < sortedMarkers.length - 1 
          ? sortedMarkers[i + 1].time 
          : slicerAudioDuration;
        
        // Extract audio segment
        const startSample = Math.floor(startTime * audioBuffer.sampleRate);
        const endSample = Math.floor(endTime * audioBuffer.sampleRate);
        const length = endSample - startSample;
        
        // Create new audio buffer for slice
        const sliceBuffer = new AudioContext().createBuffer(
          audioBuffer.numberOfChannels,
          length,
          audioBuffer.sampleRate
        );
        
        // Copy audio data
        for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
          const sourceData = audioBuffer.getChannelData(channel);
          const sliceData = sliceBuffer.getChannelData(channel);
          
          for (let j = 0; j < length; j++) {
            sliceData[j] = sourceData[startSample + j];
          }
        }
        
        // Convert to WAV blob
        const wav = audioBufferToWav(sliceBuffer);
        const blob = new Blob([wav], { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        
        // Map to MIDI note (C1 = 36, up to C8 = 96)
        const midiNote = Math.min(36 + i, 96);
        
        slices.push({
          id: crypto.randomUUID(),
          startTime,
          endTime,
          midiNote,
          audioUrl: url,
        });
      }
      
      setAudioSlices(slices);
    } catch (err) {
      setError('Failed to create slices: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsCreatingSlices(false);
    }
  };

  // Convert AudioBuffer to WAV
  const audioBufferToWav = (buffer: AudioBuffer): ArrayBuffer => {
    const length = buffer.length * buffer.numberOfChannels * 2;
    const arrayBuffer = new ArrayBuffer(44 + length);
    const view = new DataView(arrayBuffer);
    
    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, buffer.numberOfChannels, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * buffer.numberOfChannels * 2, true);
    view.setUint16(32, buffer.numberOfChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length, true);
    
    // Write audio data
    let offset = 44;
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
        offset += 2;
      }
    }
    
    return arrayBuffer;
  };

  // Re-analyze when sensitivity changes
  const handleSensitivityChange = async (newSensitivity: number) => {
    setSlicerSensitivity(newSensitivity);
    
    if (audioBufferRef.current) {
      await detectTransients(audioBufferRef.current);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Audio Slicer</h3>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm"
        >
          Load Audio
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {error && (
        <div className="p-3 bg-red-900/20 border border-red-500 rounded text-red-400 text-sm">
          {error}
        </div>
      )}

      {slicerAudioUrl && (
        <>
          {/* Sensitivity slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm text-zinc-400">Sensitivity</label>
              <span className="text-sm text-zinc-500">{Math.round(slicerSensitivity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={slicerSensitivity}
              onChange={(e) => handleSensitivityChange(parseFloat(e.target.value))}
              className="w-full"
              disabled={isAnalyzing}
            />
          </div>

          {/* Waveform display with markers */}
          <div className="space-y-2">
            <div className="text-sm text-zinc-400">
              Waveform ({sliceMarkers.length} markers)
            </div>
            <canvas
              ref={canvasRef}
              className="w-full h-32 bg-zinc-900 rounded cursor-crosshair"
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
            />
            <div className="text-xs text-zinc-500">
              Click to add markers, drag to adjust, or use auto-detect
            </div>
          </div>

          {/* Slice markers list */}
          {sliceMarkers.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm text-zinc-400">Slice Markers</div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {sliceMarkers.map((marker, index) => (
                  <div
                    key={marker.id}
                    className="flex items-center justify-between p-2 bg-zinc-800 rounded text-sm"
                  >
                    <span className="text-zinc-300">
                      Slice {index + 1}: {marker.time.toFixed(3)}s
                    </span>
                    <button
                      onClick={() => removeSliceMarker(marker.id)}
                      className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Create slices button */}
          <button
            onClick={createSlices}
            disabled={sliceMarkers.length === 0 || isCreatingSlices}
            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-zinc-700 disabled:cursor-not-allowed rounded"
          >
            {isCreatingSlices ? 'Creating Slices...' : `Create ${sliceMarkers.length} Slices`}
          </button>

          {/* Created slices list */}
          {audioSlices.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm text-zinc-400">Created Slices</div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {audioSlices.map((slice, index) => (
                  <div
                    key={slice.id}
                    className="flex items-center justify-between p-2 bg-zinc-800 rounded text-sm"
                  >
                    <div className="flex-1">
                      <div className="text-zinc-300">
                        Slice {index + 1} (MIDI Note: {slice.midiNote})
                      </div>
                      <div className="text-xs text-zinc-500">
                        {slice.startTime.toFixed(3)}s - {slice.endTime.toFixed(3)}s
                      </div>
                    </div>
                    <audio
                      src={slice.audioUrl}
                      controls
                      className="h-8"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!slicerAudioUrl && (
        <div className="text-center py-12 text-zinc-500">
          Load an audio file to start slicing
        </div>
      )}
    </div>
  );
};
